import type { Root } from "./prototypeTypes";
import type { MediaKind, MediaSurface, MountedPlayer } from "./mediaPlayerTypes";
import { attachTo, disposeMounted, ensureElement, pickContainer } from "./mediaPlayerDom";
import { maybeFinalChapterBurst, resetFinalChapterBurst } from "./mediaPlayerVideoEnded";
import { tryReusePictureInPictureVideo } from "./mediaPlayerPipReuse";
import { playerSelectors } from "./mediaPlayerSelectors";
import {
  activeAudioDurationFallback,
  activeVideoDurationFallback,
  persistAudioProgress,
  persistVideoProgress,
  playbackCurrentSeconds,
  playbackDurationSeconds,
  restoreAudioPosition,
  restoreVideoPosition,
  runtimeOf,
  setPlaybackCurrentSeconds,
  syncMediaUi,
  presentOptimisticAudioUi,
	previewVideoSeekUi,
  resetAudioPlaybackUi,
} from "./mediaPlayerProgress";
import { clearOptimisticAudioPlayback, clearOptimisticAudioPlaybackForTarget, optimisticAudioPlayback } from './audioPlaybackOptimisticIntent';
import { releaseMediaLeaseBestEffort, releaseMediaLeaseIdBestEffort, startMediaLeaseHeartbeat, stopMediaLeaseHeartbeat } from './mediaLeaseLifecycle';
import { updateVideoWatermarkTrace } from './videoWatermark';
import { paintContentTraceLabel } from './contentTraceLabel';
import { mediaSessionController } from './mediaSessionController';
import { clearNowPlayingMedia, hidePlayer, mediaSessionArtworkFor, syncNowPlayingMedia } from './playerUiController';
import { beginAudioHandoffExplicitPlayIntent, beginMediaUserIntent, canCommitAudioHandoffExplicitPlay, canFailAudioHandoff, cancelAudioHandoffForRemoteClaim, failAudioHandoff, handoffPreparedAudioOnEnded, invalidateAudioHandoffIntent, isAudioHandoffCancelled, isAudioHandoffTransitioning, markAudioHandoffStalled, observeAudioPlaying, onAudioHandoffLoadStart, onAudioMounted, onAudioTimeupdate, resetAudioHandoffCancellation } from './audioHandoffController';
import { claimAudioPlaybackOwner, registerAudioPlaybackOwner, unregisterAudioPlaybackOwner } from './audioPlaybackOwner';
import type { MediaChapterTransitionCommit } from './mediaChapterTransitionCoordinator';
import type { MediaEndedEventDetail } from './mediaPlayerEndedControls';
import { cancelVideoPictureInPictureExitHandoff, exitVideoPictureInPicturePresentation, isVideoPictureInPictureActive, observeVideoPictureInPictureExit, setVideoPictureInPictureExplicitPlaybackIntent, toggleVideoPictureInPicturePresentation } from './videoPictureInPicture';
import { syncVideoPresentationControls } from './videoPresentationControls';
import { requestExplicitPlaybackTokenWithOwnerRecovery } from './playbackAuthorizationContext';
import { clearPlaybackStartRetry, presentPlaybackStartRetry } from './mediaPlaybackFeedback';
import {
	probeSignedMediaUrlOnce,
	recordMediaElementError,
	recordPlaybackPauseDuringStart,
	recordPlaybackPausedHandoffCancel,
	recordPlaybackStartFailure,
	recordPlaybackProgressCheck,
	recordPlaybackStartSkippedExpired,
	recordPlaybackStallBlobRecovery,
	playbackLifecycleSnapshot,
	type PlaybackStartFailureInfo,
} from './clientSessionDiagnostics';

export const mounted = new WeakMap<HTMLElement, MountedPlayer>();
// Scrub-boundary emulation only: completing a drag at the literal end of the
// track counts as reaching the end. This is NOT the product completion
// threshold (MEDIA_COMPLETED_RATIO in mediaPlayerProgress, 98%): seeking
// anywhere before the true end must play normally from there.
const MEDIA_END_SEEK_THRESHOLD = 0.995;
let activeAudioContainer: HTMLElement | null = null;
let activeVideoContainer: HTMLElement | null = null;
// At most one armed autoplay-retry listener per fullscreen surface: every
// chapter transition that fails to autoplay would otherwise stack another
// listener that fires on the same touch.
const videoRetryListeners = new WeakMap<HTMLElement, (event: Event) => void>();
const NATIVE_FULLSCREEN_INTENT_EVENT = 'deepmox:native-fullscreen-intent';
const NATIVE_FULLSCREEN_DISPOSE_EVENT = 'deepmox:native-fullscreen-dispose';

function signalNativeFullscreenPlaybackIntent(video: HTMLVideoElement, playing: boolean): void {
	video.dispatchEvent(new CustomEvent<boolean>(NATIVE_FULLSCREEN_INTENT_EVENT, { detail: playing }));
}

export interface MountMediaPlayerOptions {
	topicId?: string;
	chapterIndex?: number;
	deferExistingLeaseRelease?: boolean;
}

export interface PrepareVideoMediaPlayerTransitionOptions extends MountMediaPlayerOptions {
	signal?: AbortSignal;
	timeoutMs?: number;
	/** Prevent a stale token response from replacing a newer video chapter. */
	canCommit?: () => boolean;
}

export function readSurfaceFromResponse(input: unknown): MediaSurface | null {
  if (!input || typeof input !== "object") return null;
  const obj = input as Record<string, unknown>;
  if (typeof obj.signedPlaybackUrl !== "string" || obj.signedPlaybackUrl.length === 0) return null;
  if (obj.tokenType !== "audio" && obj.tokenType !== "video") return null;
  return {
    type: obj.tokenType as MediaKind,
    signedPlaybackUrl: obj.signedPlaybackUrl,
    expiresInSeconds: typeof obj.expiresInSeconds === "number" ? obj.expiresInSeconds : 0,
    delivery: typeof obj.delivery === "string" ? obj.delivery : "unknown",
    playbackLeaseId: typeof obj.playbackLeaseId === 'string' ? obj.playbackLeaseId : undefined,
    heartbeatIntervalSeconds: typeof obj.heartbeatIntervalSeconds === 'number' ? obj.heartbeatIntervalSeconds : undefined,
		watermark: obj.watermark as { visible: boolean; traceCode: string } | undefined,
		mediaResume: isMediaResume(obj.mediaResume),
	};
}

function isMediaResume(value: unknown): { positionSeconds: number; updatedAt: string } | null {
	if (!value || typeof value !== 'object') return null;
	const resume = value as { positionSeconds?: unknown; updatedAt?: unknown };
	if (!Number.isFinite(resume.positionSeconds) || typeof resume.updatedAt !== 'string') return null;
	return { positionSeconds: Math.max(0, resume.positionSeconds as number), updatedAt: resume.updatedAt };
}

export function mountMediaPlayer(root: Root, response: unknown, options: MountMediaPlayerOptions = {}): boolean {
  const surface = readSurfaceFromResponse(response);
	const rt = runtimeOf(root);
	rememberResponseResume(rt, surface, options);
  const audioContainer = pickContainer(root, 'audio');
  if (!surface) {
    paintContentTraceLabel(audioContainer, undefined, 'audio');
    return false;
  }
  const container = pickContainer(root, surface.type);
  const existing = mounted.get(container);
  const sameLease = !surface.playbackLeaseId || surface.playbackLeaseId === existing?.playbackLeaseId;
  if (existing && existing.kind === surface.type && existing.url === surface.signedPlaybackUrl && sameLease && !isExpired(existing)) {
		existing.topicId = options.topicId ?? existing.topicId;
		existing.chapterIndex = options.chapterIndex ?? existing.chapterIndex;
		paintContentTraceLabel(audioContainer, surface.type === 'audio' ? surface.watermark?.traceCode : undefined, 'audio');
		bindMediaSession(root, existing);
		syncMediaUi(root, existing);
		if (surface.type === 'video') hidePlayer(root);
    return true;
  }
	const pictureInPictureReuse = tryReusePictureInPictureVideo(root, existing, surface, options);
	if (pictureInPictureReuse !== null) {
		if (existing) {
			existing.topicId = options.topicId ?? existing.topicId;
			existing.chapterIndex = options.chapterIndex ?? existing.chapterIndex;
			if (pictureInPictureReuse) {
				releaseOtherMediaKind(root, surface.type);
				paintContentTraceLabel(audioContainer, undefined, 'audio');
				bindMediaSession(root, existing);
				hidePlayer(root);
			}
		}
		return pictureInPictureReuse;
	}
  let element: HTMLVideoElement | HTMLAudioElement | null = null;
  try {
    element = ensureElement(surface.type);
    if (element instanceof HTMLVideoElement) element.playsInline = true;
    // Dispose the previous mount BEFORE assigning the source: the audio
    // element is reused across mounts, and disposing afterwards would pause
    // and strip the just-assigned signed source.
    disposeMedia(existing, !options.deferExistingLeaseRelease, existing?.suppressEndedMediaSessionCleanup === true);
    // VivoBrowser can retain an unattached media element at HAVE_METADATA.
    // Attach before assigning the signed source so its native media pipeline
    // observes both the source load and the subsequent user-initiated play.
    attachTo(container, element);
    element.src = surface.signedPlaybackUrl;
    element.load();
  } catch {
    element?.remove();
    return false;
  }
  if (!element) return false;
  const media = {
    element,
    kind: surface.type,
    url: surface.signedPlaybackUrl,
    topicId: options.topicId ?? rt?.activeCourseId,
    chapterIndex: options.chapterIndex ?? rt?.activeChapterIndex,
    expiresAt: Math.floor(Date.now() / 1000) + surface.expiresInSeconds,
    root: container,
    loading: false,
    playbackLeaseId: surface.playbackLeaseId,
    heartbeatIntervalSeconds: surface.heartbeatIntervalSeconds,
		traceCode: surface.watermark?.traceCode,
		claimOwnerOnNextPlaying: surface.type === 'audio',
		fallbackDurationSeconds: surface.type === "audio" ? activeAudioDurationFallback(root) : activeVideoDurationFallback(root),
		sourceGeneration: surface.type === 'video' ? (existing?.sourceGeneration ?? 0) + 1 : undefined,
  } satisfies MountedPlayer;
  mounted.set(container, media);
  setActiveContainer(surface.type, container);
  sweepStrayMediaElements(container, surface.type, media);
  releaseOtherMediaKind(root, surface.type);
	if (surface.type === 'video') hidePlayer(root);
  paintContentTraceLabel(audioContainer, surface.type === 'audio' ? surface.watermark?.traceCode : undefined, 'audio');
  resetFinalChapterBurst();
  wireMediaEvents(root, media);
	if (media.element instanceof HTMLVideoElement) syncVideoPresentationControls(root, media.element);
	if (surface.type === 'audio') onAudioMounted(root, media);
	if (surface.type === 'audio') ensureAudioPlaybackOwner(root);
	if (surface.type === 'audio') syncNowPlayingMedia(root, media);
	bindMediaSession(root, media);
  wirePagehideRelease(media);
  if (surface.type === 'video' && surface.watermark?.traceCode) updateVideoWatermarkTrace(root, surface.watermark.traceCode);
  if (surface.type === "audio") restoreAudioPosition(root, media);
  else restoreVideoPosition(root, media);
	syncMediaUi(root, media);
  return true;
}

/**
 * Register before an audio token is awaited as well as after it mounts. This
 * lets a remote explicit claim invalidate a pending first-play request, where
 * there is deliberately no local media element yet.
 */
export function ensureAudioPlaybackOwner(root: Root): void {
	registerAudioPlaybackOwner(root as unknown as object, () => {
		const current = mountedFor(root, 'audio');
		cancelAudioHandoffForRemoteClaim(root, current);
		// Keep a system-level explicit resume action available. It obtains a
		// fresh token before playback, rather than reviving a released URL.
		if (current) {
			bindMediaSession(root, current);
			mediaSessionController.activate(current);
		}
	});
}

export async function prepareVideoMediaPlayerTransition(
	root: Root,
	response: unknown,
	options: PrepareVideoMediaPlayerTransitionOptions = {},
): Promise<MediaChapterTransitionCommit> {
	const surface = readSurfaceFromResponse(response);
	if (!surface || surface.type !== 'video' || options.signal?.aborted || options.canCommit?.() === false) return { committed: false };
	const container = pickContainer(root, 'video');
	const existing = mounted.get(container);
	if (
		existing?.kind === 'video'
		&& existing.element instanceof HTMLVideoElement
		&& isVideoPictureInPictureActive(existing.element)
	) {
		return preparePictureInPictureTransition(root, existing, surface, options);
	}

	const candidate = ensureElement('video') as HTMLVideoElement;
	const ready = await loadVideoCandidate(candidate, surface.signedPlaybackUrl, options.signal, options.timeoutMs);
	if (!ready || options.signal?.aborted || options.canCommit?.() === false) {
		discardVideoCandidate(candidate);
		return { committed: false };
	}

	try {
		if (options.canCommit?.() === false) {
			discardVideoCandidate(candidate);
			return { committed: false };
		}
		attachTo(container, candidate);
	} catch {
		discardVideoCandidate(candidate);
		return { committed: false };
	}
	const rt = runtimeOf(root);
	rememberResponseResume(rt, surface, options);
	const media = {
		element: candidate,
		kind: 'video',
		url: surface.signedPlaybackUrl,
		topicId: options.topicId ?? rt?.activeCourseId,
		chapterIndex: options.chapterIndex ?? rt?.activeChapterIndex,
		expiresAt: Math.floor(Date.now() / 1000) + surface.expiresInSeconds,
		root: container,
		loading: false,
		playbackLeaseId: surface.playbackLeaseId,
		heartbeatIntervalSeconds: surface.heartbeatIntervalSeconds,
		traceCode: surface.watermark?.traceCode,
		fallbackDurationSeconds: activeVideoDurationFallback(root),
		sourceGeneration: (existing?.sourceGeneration ?? 0) + 1,
	} satisfies MountedPlayer;
	const previousLeaseId = existing?.playbackLeaseId;
	disposeMedia(existing, false, existing?.suppressEndedMediaSessionCleanup === true);
	mounted.set(container, media);
	setActiveContainer('video', container);
	paintContentTraceLabel(pickContainer(root, 'audio'), undefined, 'audio');
	resetFinalChapterBurst();
	wireMediaEvents(root, media);
	if (media.element instanceof HTMLVideoElement) syncVideoPresentationControls(root, media.element);
	bindMediaSession(root, media);
	wirePagehideRelease(media);
	if (surface.watermark?.traceCode) updateVideoWatermarkTrace(root, surface.watermark.traceCode);
	restoreVideoPosition(root, media);
	return {
		committed: true,
		previousLeaseId: previousLeaseId && previousLeaseId !== media.playbackLeaseId ? previousLeaseId : undefined,
	};
}

function rememberResponseResume(
	runtime: ReturnType<typeof runtimeOf>,
	surface: MediaSurface | null,
	options: MountMediaPlayerOptions,
): void {
	if (!runtime || !surface?.mediaResume || !options.topicId || typeof options.chapterIndex !== 'number') return;
	const key = `${options.topicId}:${options.chapterIndex}`;
	// An explicit local switch has already captured a newer currentTime. A late
	// token response must not rewind it to the database snapshot.
	if (typeof runtime.mediaResumeSecondsByChapter?.[key] === 'number') return;
	runtime.mediaResumeSecondsByChapter = {
		...(runtime.mediaResumeSecondsByChapter ?? {}),
		[key]: surface.mediaResume.positionSeconds,
	};
}

export async function playMediaPlayer(root: Root, kind: MediaKind): Promise<boolean> {
  const media = mountedFor(root, kind);
	if (!media) return false;
  pauseOtherKind(root, kind);
	if (kind === 'audio') {
		const intent = beginAudioHandoffExplicitPlayIntent(root);
		if (intent.target) {
			const runtime = runtimeOf(root);
			const api = runtime?.api;
			const plan = runtime?.currentPlan;
			const target = intent.target;
			if (!api || !plan) return false;
			try {
				// A cancelled handoff retains its mounted element so this explicit user
				// resume comes through the core rather than requestAudioPlayback. It
				// still gets the same single stale-owner takeover attempt.
				const response = await requestExplicitPlaybackTokenWithOwnerRecovery(
					() => api.audioPlaybackToken({ topicId: target.topicId, chapterIndex: target.chapterIndex, plan }),
				);
				if (!canCommitAudioHandoffExplicitPlay(root, intent)) {
					releaseMediaLeaseIdBestEffort(readSurfaceFromResponse(response)?.playbackLeaseId);
					return false;
				}
				if (!mountMediaPlayer(root, response, intent.target)) {
					releaseMediaLeaseIdBestEffort(readSurfaceFromResponse(response)?.playbackLeaseId);
					return false;
				}
				resetAudioHandoffCancellation(root);
				return await playMediaPlayer(root, 'audio');
			} catch { return false; }
		}
		if (isExpired(media)) {
			recordPlaybackStartSkippedExpired({ mediaKind: kind, expiresSkewSeconds: media.expiresAt > 0 ? Math.round(media.expiresAt - Date.now() / 1000) : undefined });
			return false;
		}
		// A fresh explicit start may recover a source that a previous user pause
		// deliberately cancelled. The earlier pending call keeps its own guard.
		media.audioFrozenStartRecoveryCancelled = false;
		presentOptimisticAudioPlayback(root, { topicId: media.topicId, chapterIndex: media.chapterIndex, paused: false });
		media.claimOwnerOnNextPlaying = true;
	} else {
		// An explicit video start/resume is newer user intent than any pending
		// audio chapter token; do not let that token return and pause video.
		invalidateAudioHandoffIntent(root);
		if (isExpired(media)) {
			recordPlaybackStartSkippedExpired({ mediaKind: kind, expiresSkewSeconds: media.expiresAt > 0 ? Math.round(media.expiresAt - Date.now() / 1000) : undefined });
			return recoverVideoPlayback(root, media, 'expired');
		}
		signalNativeFullscreenPlaybackIntent(media.element as HTMLVideoElement, true);
	}
	const playStartedAt = Date.now();
	const lifecycleAtPlay = playbackLifecycleSnapshot(mediaDocument(media));
	try {
		media.playbackStartPending = true;
		await playMediaElementWithStallRecovery(root, media, kind);
		clearPlaybackStartRetry(root, kind);
		return true;
	} catch (error) {
		if (kind === 'audio') clearOptimisticAudioPlaybackForTarget(root, media);
		const failureInfo = playbackFailureInfo(media, kind, error, playStartedAt, lifecycleAtPlay);
		recordPlaybackStartFailure(failureInfo);
		probeSignedMediaUrlOnce(media.url);
		const frozenRecovery = kind === 'audio' && isFrameFrozenInterruption(error)
			? recoverFrozenAudioStartOnce(root, media)
			: null;
		if (frozenRecovery) return frozenRecovery;
		if (kind === 'video' && isVideoRecoveryEligible(media, error, true)) return recoverVideoPlayback(root, media, 'play_failure');
		// A delayed mobile-browser play() can lose its transient activation.
		// Keep the mounted player visible, synchronise its paused state, and
		// provide a real second-tap recovery instead of claiming autoplay worked.
		if (mountedFor(root, kind) === media) {
			syncMediaUi(root, media);
			// A play() that our own user pause aborted is the pause working as
			// intended, not a failed start: it must not raise the retry toast.
			const userPausedRecently = typeof media.userPauseIntentAt === 'number' && Date.now() - media.userPauseIntentAt < 1500;
			if (!userPausedRecently) presentPlaybackStartRetry(root, kind, () => { void playMediaPlayer(root, kind); });
		}
		return false;
	} finally {
		if (mountedFor(root, kind) === media) media.playbackStartPending = false;
	}
}

function mediaDocument(media: MountedPlayer): Document {
	return media.root.ownerDocument ?? document;
}

/** VivoBrowser surfaces this exact AbortError when a trusted play is interrupted by Page Lifecycle freeze. */
function isFrameFrozenInterruption(error: unknown): boolean {
	const message = error instanceof Error || error instanceof DOMException
		? error.message
		: error && typeof error === 'object' && typeof (error as { message?: unknown }).message === 'string'
			? (error as { message: string }).message
			: '';
	return /containing page was frozen/i.test(message);
}

/**
 * A frozen page can reject the original trusted play after the user returns.
 * Keep exactly one recovery intent on the same mounted audio source. It never
 * mints, renews, releases, or polls; a source switch, user pause, or expiry
 * wins immediately and leaves the normal explicit Listen path available.
 */
function recoverFrozenAudioStartOnce(root: Root, media: MountedPlayer): Promise<boolean> | null {
	if (media.kind !== 'audio' || mountedFor(root, 'audio') !== media || media.audioFrozenStartRecoveryAttempted || isExpired(media)) return null;
	if (media.audioFrozenStartRecoveryCancelled) return null;
	media.audioFrozenStartRecoveryAttempted = true;
	const doc = mediaDocument(media);
	const view = doc.defaultView;
	let settled = false;
	let retryStarted = false;
	let resolveRecovery!: (played: boolean) => void;
	const finish = (played: boolean) => {
		if (settled) return;
		settled = true;
		doc.removeEventListener('visibilitychange', resume, true);
		doc.removeEventListener('resume', resume, true);
		view?.removeEventListener('pageshow', resume, true);
		media.audioFrozenStartRecoveryCleanup = undefined;
		resolveRecovery(played);
	};
	const retry = async () => {
		if (settled || retryStarted) return;
		retryStarted = true;
		if (media.audioFrozenStartRecoveryCancelled || mountedFor(root, 'audio') !== media || isExpired(media) || doc.visibilityState !== 'visible') {
			finish(false);
			return;
		}
		const playStartedAt = Date.now();
		const lifecycleAtPlay = playbackLifecycleSnapshot(doc);
		try {
			await playMediaElementWithPauseTrace(media, 'audio');
			clearPlaybackStartRetry(root, 'audio');
			finish(true);
		} catch (error) {
			clearOptimisticAudioPlaybackForTarget(root, media);
			recordPlaybackStartFailure(playbackFailureInfo(media, 'audio', error, playStartedAt, lifecycleAtPlay));
			if (mountedFor(root, 'audio') === media) {
				syncMediaUi(root, media);
				presentPlaybackStartRetry(root, 'audio', () => { void playMediaPlayer(root, 'audio'); });
			}
			finish(false);
		}
	};
	const resume = () => {
		if (doc.visibilityState === 'visible') void retry();
	};
	const recovery = new Promise<boolean>((resolve) => { resolveRecovery = resolve; });
	media.audioFrozenStartRecoveryPromise = recovery;
	media.audioFrozenStartRecoveryCleanup = () => {
		media.audioFrozenStartRecoveryCancelled = true;
		finish(false);
	};
	doc.addEventListener('visibilitychange', resume, true);
	doc.addEventListener('resume', resume, true);
	view?.addEventListener('pageshow', resume, true);
	if (doc.visibilityState === 'visible') queueMicrotask(() => { void retry(); });
	return recovery.finally(() => {
		if (media.audioFrozenStartRecoveryPromise === recovery) media.audioFrozenStartRecoveryPromise = undefined;
	});
}

function invalidateFrozenAudioStartRecovery(media: MountedPlayer | undefined): void {
	if (!media || media.kind !== 'audio') return;
	media.audioFrozenStartRecoveryCancelled = true;
	media.audioFrozenStartRecoveryCleanup?.();
}

/**
 * Android's AbortError does not identify the pause caller. Wrap only the
 * short pending play() window: an invocation seen here is application code;
 * no record means the browser/media engine cancelled it itself. The original
 * method is restored before any recovery or normal playback work continues.
 */
async function playMediaElementWithPauseTrace(media: MountedPlayer, kind: MediaKind): Promise<void> {
	const element = media.element;
	const originalPause = element.pause;
	const originalOwnPause = Object.getOwnPropertyDescriptor(element, 'pause');
	let pending = true;
	const tracedPause = function(this: HTMLMediaElement): void {
		if (pending) recordPlaybackPauseDuringStart({ mediaKind: kind, pauseOrigin: playbackPauseOrigin() });
		return originalPause.call(this);
	};
	try {
		Object.defineProperty(element, 'pause', { configurable: true, writable: true, value: tracedPause });
	} catch {
		// Some browser implementations expose a non-overridable native method.
		await element.play();
		return;
	}
	try {
		await element.play();
	} finally {
		pending = false;
		if (element.pause === tracedPause) {
			if (originalOwnPause) Object.defineProperty(element, 'pause', originalOwnPause);
			else Reflect.deleteProperty(element, 'pause');
		}
	}
}

function playbackPauseOrigin(): string | undefined {
	const stack = new Error().stack;
	if (!stack) return undefined;
	return stack.split('\n').slice(2, 5).map((line) => line.trim()).filter(Boolean).join(' | ');
}

const AUDIO_STALL_RECOVERY_MS = 4000;
// Keep the retry window equal to the general one: a healthy cold start can
// legitimately wait longer than 1.5s for its first data, and an early engage
// forces a whole-file blob download on devices that were about to succeed.
// The Vivo freeze path still recovers instantly via the resume/visibility wake.
const AUDIO_STALL_RECOVERY_RETRY_MS = 4000;
const AUDIO_STALL_RECOVERY_PLAY_TIMEOUT_MS = 12000;
// Video metadata travels with the moov atom and is slower to reach than audio;
// a healthy-but-slow phone must not be pushed into a whole-file blob download,
// so the video stall window is longer before the same fallback engages.
const VIDEO_STALL_RECOVERY_MS = 8000;
const VIDEO_STALL_RECOVERY_PLAY_TIMEOUT_MS = 30000;

/**
 * Topics whose audio start has already stalled once on this device. The
 * vendor media stack failure is deterministic, so later mounts of the same
 * topic only get the short stall window before the blob fallback engages.
 */
const stallProneAudioTopics = new WeakMap<Root, Set<string>>();

function markStallProneAudioTopic(root: Root, topicId: string | undefined): void {
	if (!topicId) return;
	let topics = stallProneAudioTopics.get(root);
	if (!topics) {
		topics = new Set();
		stallProneAudioTopics.set(root, topics);
	}
	if (topics.size >= 8) return;
	topics.add(topicId);
}

const PLAYBACK_PROGRESS_CHECK_DELAY_MS = 2000;
const PLAYBACK_PROGRESS_SAMPLE_MS = 1000;

/**
 * A resolved play() can still be a silent fake on a broken vendor stack.
 * Two currentTime samples answer whether the recovered start really plays;
 * a non-advancing clock redirects the fix toward the audio output path.
 */
function schedulePlaybackProgressCheck(root: Root, media: MountedPlayer): void {
	const element = media.element;
	window.setTimeout(() => {
		if (mountedFor(root, 'audio') !== media || element.error) return;
		const firstSampleSeconds = element.currentTime;
		window.setTimeout(() => {
			if (mountedFor(root, 'audio') !== media) return;
			recordPlaybackProgressCheck({
				mediaKind: 'audio',
				advancing: element.currentTime > firstSampleSeconds,
				deltaMs: Math.round((element.currentTime - firstSampleSeconds) * 1000),
				currentTimeSeconds: Math.round(element.currentTime * 1000) / 1000,
				readyState: element.readyState,
				paused: element.paused,
				volume: Math.round(element.volume * 100) / 100,
				muted: element.muted,
			});
		}, PLAYBACK_PROGRESS_SAMPLE_MS);
	}, PLAYBACK_PROGRESS_CHECK_DELAY_MS);
}

function canFetchAsBlob(url: string): boolean {
	try {
		const parsed = new URL(url, document.baseURI);
		return (parsed.protocol === 'https:' || parsed.protocol === 'http:') && parsed.origin === document.location.origin;
	} catch {
		return false;
	}
}

/**
 * Some vendor Android media stacks (observed on VivoBrowser) take the
 * element's own network path and hold a same-origin source at HAVE_METADATA
 * forever, while an in-page fetch of the same signed URL succeeds in under a
 * second. After a bounded stall on a visible page, refetch the source as a
 * blob through the ordinary fetch stack and feed the element from memory,
 * bypassing the vendor media network stack entirely. One attempt per mount.
 * The same failure shape hits video starts, so the fallback is shared; video
 * waits longer before engaging (metadata + moov travel) and preserves the
 * resume position across the source swap.
 */
async function playMediaElementWithStallRecovery(root: Root, media: MountedPlayer, kind: MediaKind): Promise<void> {
	const element = media.element;
	const blobRecoveryAttempted = kind === 'audio' ? media.audioBlobRecoveryAttempted : media.videoBlobRecoveryAttempted;
	if (blobRecoveryAttempted || !canFetchAsBlob(media.url)) {
		await playMediaElementWithPauseTrace(media, kind);
		return;
	}
	if (kind === 'audio') media.audioBlobRecoveryAttempted = true;
	else media.videoBlobRecoveryAttempted = true;
	// A video source swap must not fight the chapter pump: recovery only arms
	// while this mount owns the element at the captured operation generation.
	const armOperationGeneration = media.videoSourceOperationGeneration;
	const videoStillOwned = () => kind !== 'video'
		|| (mountedFor(root, 'video') === media && media.videoSourceOperationGeneration === armOperationGeneration);
	const pendingPlay = playMediaElementWithPauseTrace(media, kind);
	pendingPlay.catch(() => undefined);
	let engaged = false;
	let engageGate: (value: 'recovery') => void = () => undefined;
	const recoveryGate = new Promise<'recovery'>((resolve) => { engageGate = resolve; });
	let recovery: Promise<void> | null = null;
	let recoveryStartedAt = 0;
	const maybeEngage = () => {
		if (engaged || settled) return;
		// The vendor stack stalls in two shapes: NETWORK_IDLE at HAVE_METADATA
		// and NETWORK_LOADING that never reaches HAVE_CURRENT_DATA. Only the
		// element error and a running clock disqualify recovery.
		if (element.readyState >= 2 || element.error) return;
		// A hidden page pauses media by itself; recovery only makes sense while
		// the user is still looking at a stalled start.
		if (mediaDocument(media).visibilityState !== 'visible') return;
		if (!videoStillOwned()) return;
		const startedAt = Date.now();
		recoveryStartedAt = startedAt;
		recordPlaybackStallBlobRecovery({ mediaKind: kind, phase: 'engaged' });
		engaged = true;
		// The blob download can outlast a user's patience. From here the swapped
		// source starts paused, so a tap is a normal toggle again instead of a
		// silent retry — a long pending window must never eat the transport.
		media.playbackStartPending = false;
		if (kind === 'audio') markStallProneAudioTopic(root, media.topicId);
		recovery = (async () => {
			const response = await fetch(media.url, { credentials: 'same-origin' });
			if (!response.ok) throw new Error(`blob recovery HTTP ${response.status}`);
			const blob = await response.blob();
			const blobUrl = URL.createObjectURL(blob);
			if (kind === 'video') {
				// The swap resets the element to 0:00. Re-arm the resume so the
				// loadedmetadata restore seeks this session's position back.
				const resumeSeconds = playbackCurrentSeconds(media);
				const key = media.topicId && typeof media.chapterIndex === 'number' ? `${media.topicId}:${media.chapterIndex}` : undefined;
				const runtime = runtimeOf(root);
				if (resumeSeconds > 0 && key && runtime) {
					runtime.mediaResumeSecondsByChapter = { ...(runtime.mediaResumeSecondsByChapter ?? {}), [key]: resumeSeconds };
					media.resumeApplied = false;
				}
			}
			if (media.blobSourceUrl) URL.revokeObjectURL(media.blobSourceUrl);
			media.blobSourceUrl = blobUrl;
			element.src = blobUrl;
			element.load();
			await playMediaElementWithPauseTrace(media, kind);
			recordPlaybackStallBlobRecovery({ mediaKind: kind, phase: 'succeeded', recoveryMs: Date.now() - startedAt });
			schedulePlaybackProgressCheck(root, media);
		})();
		recovery.catch(() => undefined);
		engageGate('recovery');
	};
	const stallTopicId = media.topicId;
	const stallTimer = window.setTimeout(maybeEngage, kind === 'video'
		? VIDEO_STALL_RECOVERY_MS
		: stallTopicId && stallProneAudioTopics.get(root)?.has(stallTopicId) ? AUDIO_STALL_RECOVERY_RETRY_MS : AUDIO_STALL_RECOVERY_MS);
	// VivoBrowser suspends the whole renderer while the page still looks visible;
	// the stall timer only wakes when the freeze lifts. Re-check immediately when
	// the page becomes visible again or resumes from a freeze.
	const stallDoc = mediaDocument(media);
	const wakeRecovery = () => {
		if (stallDoc.visibilityState === 'visible') maybeEngage();
	};
	stallDoc.addEventListener('visibilitychange', wakeRecovery);
	stallDoc.addEventListener('resume', wakeRecovery);
	let settled = false;
	try {
		// The stall timer cannot have fired yet, so this race is normally won
		// by a healthy play(). A stalled one is overtaken by the recovery gate.
		let winner: 'play' | 'recovery';
		try {
			winner = await Promise.race([pendingPlay.then(() => 'play' as const), recoveryGate]);
		} catch (error) {
			if (!engaged) throw error;
			// The blob source swap aborts the original play(); its rejection no
			// longer matters — the recovery result is authoritative.
			winner = 'recovery';
		}
		settled = true;
		if (winner === 'play' || !recovery) return;
		await Promise.race([
			recovery,
			new Promise<never>((_, reject) => window.setTimeout(
				() => reject(new DOMException('Blob recovery timed out', 'AbortError')),
				kind === 'video' ? VIDEO_STALL_RECOVERY_PLAY_TIMEOUT_MS : AUDIO_STALL_RECOVERY_PLAY_TIMEOUT_MS,
			)),
		]);
	} catch (error) {
		if (engaged) {
			recordPlaybackStallBlobRecovery({
				mediaKind: kind,
				phase: 'failed',
				recoveryMs: Math.max(0, Date.now() - recoveryStartedAt),
				errorName: error instanceof Error ? error.name : undefined,
				errorMessage: error instanceof Error ? error.message : undefined,
			});
		}
		throw error;
	} finally {
		// Cleared on every exit path, including a healthy start.
		clearTimeout(stallTimer);
		stallDoc.removeEventListener('visibilitychange', wakeRecovery);
		stallDoc.removeEventListener('resume', wakeRecovery);
	}
}

function playbackFailureInfo(
	media: MountedPlayer,
	kind: MediaKind,
	error: unknown,
	playStartedAt?: number,
	lifecycleAtPlay?: ReturnType<typeof playbackLifecycleSnapshot>,
): PlaybackStartFailureInfo {
	const element = media.element;
	return {
		mediaKind: kind,
		errorName: error instanceof DOMException || error instanceof Error ? error.name : error !== undefined ? String(error) : undefined,
		errorMessage: error instanceof Error && error.message ? error.message : undefined,
		mediaErrorCode: element.error?.code,
		mediaErrorMessage: element.error?.message,
		networkState: element.networkState,
		readyState: element.readyState,
		expiresSkewSeconds: media.expiresAt > 0 ? Math.round(media.expiresAt - Date.now() / 1000) : undefined,
		playElapsedMs: playStartedAt === undefined ? undefined : Math.max(0, Date.now() - playStartedAt),
		lifecycleAtPlay,
		lifecycleAtFailure: playbackLifecycleSnapshot(mediaDocument(media)),
	};
}

function isVideoRecoveryEligible(media: MountedPlayer, error: unknown, explicit = false): boolean {
	if (media.kind !== 'video' || (!explicit && media.videoRecoveryAttempted)) return false;
	if (isExpired(media) || media.element.error) return true;
	const name = error instanceof DOMException ? error.name : error instanceof Error ? error.name : '';
	// NotAllowedError means the browser needs a later user gesture, not a new
	// token. Refreshing there creates a loop without restoring playback.
	return name === 'NetworkError';
}

/** One bounded, identity-guarded refresh for an expired/broken video source. */
async function recoverVideoPlayback(root: Root, media: MountedPlayer, reason: 'expired' | 'play_failure' | 'media_error'): Promise<boolean> {
	const explicit = reason === 'expired' || reason === 'play_failure';
	if (media.kind !== 'video' || mountedFor(root, 'video') !== media || (!explicit && media.videoRecoveryAttempted) || !media.topicId || !media.chapterIndex) return false;
	if (media.videoRecoveryPromise) return media.videoRecoveryPromise;
	const recovery = recoverVideoPlaybackOnce(root, media, media.topicId, media.chapterIndex);
	media.videoRecoveryPromise = recovery;
	try {
		return await recovery;
	} finally {
		if (media.videoRecoveryPromise === recovery) media.videoRecoveryPromise = undefined;
	}
}

async function recoverVideoPlaybackOnce(root: Root, media: MountedPlayer, topicId: string, chapterIndex: number): Promise<boolean> {
	clearVideoStallRecovery(media);
	const runtime = runtimeOf(root);
	if (!runtime?.api || !runtime.currentPlan) return false;
	media.videoRecoveryAttempted = true;
	const targetSeconds = playbackCurrentSeconds(media);
	const playbackRate = media.element.playbackRate;
	const sourceGeneration = media.sourceGeneration;
	const canCommit = () => mountedFor(root, 'video') === media
		&& media.topicId === topicId
		&& media.chapterIndex === chapterIndex
		&& media.sourceGeneration === sourceGeneration;
	try {
		const response = await runtime.api.videoPlaybackToken({ topicId, chapterIndex, plan: runtime.currentPlan });
		if (!canCommit()) {
			releaseMediaLeaseIdBestEffort(readSurfaceFromResponse(response)?.playbackLeaseId);
			return false;
		}
		const result = await prepareVideoMediaPlayerTransition(root, response, { topicId, chapterIndex, canCommit });
		if (!result.committed) {
			releaseMediaLeaseIdBestEffort(readSurfaceFromResponse(response)?.playbackLeaseId);
			return false;
		}
		if (result.previousLeaseId) releaseMediaLeaseIdBestEffort(result.previousLeaseId);
		const recovered = mountedFor(root, 'video');
		if (!recovered || recovered.topicId !== topicId || recovered.chapterIndex !== chapterIndex) return false;
		recovered.videoRecoveryAttempted = true;
		// The fresh signed source has not had its blob attempt yet; without this
		// reset a re-mount that raced the armed blob timer would leave the new
		// source with no vendor-stack fallback at all.
		recovered.videoBlobRecoveryAttempted = false;
		recovered.element.playbackRate = Number.isFinite(playbackRate) && playbackRate > 0 ? playbackRate : 1;
		setPlaybackCurrentSeconds(recovered, targetSeconds);
		syncMediaUi(root, recovered);
		// This source is deliberately marked recovered so automatic errors cannot
		// loop forever. Resume it directly instead of recursively recovering.
		pauseOtherKind(root, 'video');
		signalNativeFullscreenPlaybackIntent(recovered.element as HTMLVideoElement, true);
		try {
			await recovered.element.play();
			return true;
		} catch {
			syncMediaUi(root, recovered);
			return false;
		}
	} catch {
		// The one attempt remains consumed for this source. A fresh chapter/source
		// resets it; this prevents an offline browser from spinning requests.
		return false;
	}
}

function scheduleVideoStallRecovery(root: Root, media: MountedPlayer): void {
	if (media.kind !== 'video' || media.videoRecoveryAttempted || media.videoStallRecoveryTimer !== undefined || media.element.paused) return;
	const sourceGeneration = media.sourceGeneration;
	const cancel = () => clearVideoStallRecovery(media);
	media.videoStallRecoveryCleanup = () => {
		media.element.removeEventListener('playing', cancel);
		media.element.removeEventListener('canplay', cancel);
		media.element.removeEventListener('pause', cancel);
	};
	media.element.addEventListener('playing', cancel, { once: true });
	media.element.addEventListener('canplay', cancel, { once: true });
	media.element.addEventListener('pause', cancel, { once: true });
	media.videoStallRecoveryTimer = globalThis.setTimeout(() => {
		media.videoStallRecoveryTimer = undefined;
		media.videoStallRecoveryCleanup?.();
		media.videoStallRecoveryCleanup = undefined;
		const stillBlocked = media.element.readyState < HTMLMediaElement.HAVE_FUTURE_DATA && !media.element.paused;
		if (mountedFor(root, 'video') !== media || media.sourceGeneration !== sourceGeneration || !stillBlocked) return;
		void recoverVideoPlayback(root, media, 'media_error');
	}, 1_500) as unknown as number;
}

function clearVideoStallRecovery(media: MountedPlayer): void {
	if (media.videoStallRecoveryTimer !== undefined) clearTimeout(media.videoStallRecoveryTimer);
	media.videoStallRecoveryTimer = undefined;
	media.videoStallRecoveryCleanup?.();
	media.videoStallRecoveryCleanup = undefined;
}

/**
 * Play the mounted video, and if the browser rejects play() — e.g. iOS
 * Safari's autoplay policy after the swipe gesture's transient activation
 * window expires during the async chapter transition — retry on the next
 * touch of the video surface. The user's swipe is an explicit "keep
 * listening" signal, so a subsequent tap on the video resumes playback
 * instead of silently staying paused. Taps on controls keep their own
 * handlers (the retry skips them) and the listener is removed once playback
 * actually starts.
 */
export function playVideoWithGestureRetry(root: Root): Promise<void> {
	const media = mountedFor(root, 'video');
	if (!media || isExpired(media)) return Promise.resolve();
	pauseOtherKind(root, 'video');
	return media.element.play().catch(() => {
		const video = media.element as HTMLVideoElement;
		if (!video.paused || mountedFor(root, 'video') !== media) return;
		const container = (root instanceof Document ? root : root.ownerDocument ?? document).getElementById(playerSelectors.video.container);
		if (!container) return;
		// The <video> element has pointer-events:none (iOS freeze fix), so a
		// retry listener cannot live on the element — it would never fire. Arm a
		// capture-phase listener on the fullscreen surface instead. The current
		// mounted video is resolved at touch time, so the retry survives the
		// element swap on the next chapter transition.
		const retryOnTouch = (event: Event) => {
			const target = event.target as Element | null;
			if (target && typeof target.closest === 'function' && target.closest('button, a, input, select, textarea, [data-action]')) return;
			const latest = mountedFor(root, 'video');
			if (!latest || !latest.element.paused) {
				container.removeEventListener('pointerdown', retryOnTouch, true);
				if (videoRetryListeners.get(container) === retryOnTouch) videoRetryListeners.delete(container);
				return;
			}
			void latest.element.play().catch(() => {
				if (mountedFor(root, 'video') === latest) {
					presentPlaybackStartRetry(root, 'video', () => { void playMediaPlayer(root, 'video'); });
				}
			});
		};
		const previous = videoRetryListeners.get(container);
		if (previous) container.removeEventListener('pointerdown', previous, true);
		videoRetryListeners.set(container, retryOnTouch);
		container.addEventListener('pointerdown', retryOnTouch, true);
	});
}

export function pauseMediaPlayer(root: Root, kind: MediaKind) {
  const media = mountedFor(root, kind);
	if (kind === 'audio') invalidateFrozenAudioStartRecovery(media);
	if (media?.element instanceof HTMLVideoElement) {
		cancelVideoPictureInPictureExitHandoff(media.element);
		setVideoPictureInPictureExplicitPlaybackIntent(media.element, false);
		signalNativeFullscreenPlaybackIntent(media.element, false);
	}
  media?.element.pause();
}

/** Explicit UI / lock-screen pause supersedes in-flight audio navigation. */
export function pauseMediaPlayerFromUser(root: Root, kind: MediaKind): void {
	if (kind === 'audio') invalidateAudioHandoffIntent(root);
	else beginMediaUserIntent(root);
	const media = mountedFor(root, kind);
	if (kind === 'audio') invalidateFrozenAudioStartRecovery(media);
	if (media) media.userPauseIntentAt = Date.now();
	if (kind === 'audio' && media) {
		presentOptimisticAudioPlayback(root, { topicId: media.topicId, chapterIndex: media.chapterIndex, paused: true });
		queueMicrotask(() => {
			if (mountedFor(root, 'audio') === media && !media.element.paused) {
				clearOptimisticAudioPlayback(root);
				syncMediaUi(root, media);
			}
		});
	}
	if (media?.element instanceof HTMLVideoElement) {
		cancelVideoPictureInPictureExitHandoff(media.element);
		setVideoPictureInPictureExplicitPlaybackIntent(media.element, false);
		signalNativeFullscreenPlaybackIntent(media.element, false);
	}
	media?.element.pause();
}

export function presentOptimisticAudioPlayback(root: Root, state: { topicId?: string; chapterIndex?: number; chapterTitle?: string; paused: boolean }): void {
	presentOptimisticAudioUi(root, state, mountedFor(root, 'audio')?.root);
}

export function rollbackOptimisticAudioPlayback(root: Root, target?: { topicId?: string; chapterIndex?: number }): void {
	if (target && !clearOptimisticAudioPlaybackForTarget(root, target)) return;
	if (!target) clearOptimisticAudioPlayback(root);
	const media = mountedFor(root, 'audio');
	if (media) syncMediaUi(root, media);
	else resetAudioPlaybackUi(root);
}

export function refreshMediaSessionPlayer(root: Root, kind: MediaKind): void {
	const media = mountedFor(root, kind);
	if (media) bindMediaSession(root, media);
}

export function activateMediaSessionPlayer(root: Root, kind: MediaKind): void {
	const media = mountedFor(root, kind);
	if (!media) return;
	bindMediaSession(root, media);
	mediaSessionController.activate(media);
}

export function toggleMediaPlayer(root: Root, kind: MediaKind) {
  const media = mountedFor(root, kind);
  if (!media) return;
	// A browser may leave play() pending for seconds. A follow-up trusted tap is
	// a retry, never a request to pause the very source that is still starting.
	if (media.playbackStartPending) {
		if (media.element instanceof HTMLVideoElement) setVideoPictureInPictureExplicitPlaybackIntent(media.element, true);
		void playMediaPlayer(root, kind);
		return;
	}
	const optimistic = kind === 'audio' ? optimisticAudioPlayback(root) : undefined;
	const visuallyPaused = optimistic && optimistic.topicId === media.topicId && optimistic.chapterIndex === media.chapterIndex
		? optimistic.paused
		: media.element.paused;
	if (visuallyPaused) {
		if (media.element instanceof HTMLVideoElement) setVideoPictureInPictureExplicitPlaybackIntent(media.element, true);
    pauseOtherKind(root, kind);
		void playMediaPlayer(root, kind);
		return;
  }
	else {
		pauseMediaPlayerFromUser(root, kind);
		if (kind === 'audio') syncMediaUi(root, media);
	}
}

export function seekMediaPlayer(root: Root, kind: MediaKind, fraction: number) {
  const media = mountedFor(root, kind);
  if (!media) return;
  const duration = playbackDurationSeconds(media);
  if (duration <= 0) return;
  const clamped = Math.min(1, Math.max(0, fraction));
  if (clamped < 1) media.endBoundaryDispatched = false;
  // Seeking back into the final chapter re-arms the fireworks/replay burst so
  // a re-listen ending again celebrates consistently (see issue: replaying the
  // last chapter must present the same finale every time).
  if (media.kind === "video" && clamped < 1) resetFinalChapterBurst();
  setPlaybackCurrentSeconds(media, duration * clamped);
  syncMediaUi(root, media);
  if (media.kind === "audio") persistAudioProgress(root, media, true);
  else persistVideoProgress(root, media, true);
}

/**
 * UI-only seek preview used while the user drags the scrubber. It deliberately
 * leaves element.currentTime and virtualCurrentTime unchanged; release commits
 * exactly once through completeMediaPlayerSeek.
 */
export function previewMediaPlayerSeek(root: Root, kind: MediaKind, fraction: number) {
  const media = mountedFor(root, kind);
  if (!media) return;
  const duration = playbackDurationSeconds(media);
  if (duration <= 0) return;
  const clamped = Math.min(1, Math.max(0, fraction));
	previewVideoSeekUi(root, media, duration * clamped);
}

/** Commit a user seek. Reaching the exact end is a playback boundary even in
 * browsers that do not emit a native `ended` event after assigning currentTime. */
export function completeMediaPlayerSeek(root: Root, kind: MediaKind, fraction: number): boolean {
  const media = mountedFor(root, kind);
  if (!media) return false;
  if (playbackDurationSeconds(media) <= 0) return false;
  const clamped = Math.min(1, Math.max(0, fraction));
  const reachesEnd = clamped >= MEDIA_END_SEEK_THRESHOLD;
  seekMediaPlayer(root, kind, reachesEnd ? 1 : clamped);
	if (!reachesEnd) return false;
	if (media.element instanceof HTMLVideoElement) {
		cancelVideoPictureInPictureExitHandoff(media.element);
		setVideoPictureInPictureExplicitPlaybackIntent(media.element, false);
	}
  media.element.pause();
  dispatchMediaEnded(root, media);
  return true;
}

export function seekMediaPlayerBySeconds(root: Root, kind: MediaKind, deltaSeconds: number) {
  const media = mountedFor(root, kind);
  if (!media) return;
  const duration = playbackDurationSeconds(media);
  const current = playbackCurrentSeconds(media);
  const max = duration > 0 ? duration : Number.MAX_SAFE_INTEGER;
  const next = Math.min(max, Math.max(0, current + deltaSeconds));
  // Rewinding away from the final chapter's end re-arms the fireworks/replay
  // burst, matching the scrubber behavior so any re-listen ends with the finale.
  if (media.kind === "video" && duration > 0 && next < duration) resetFinalChapterBurst();
  setPlaybackCurrentSeconds(media, next);
  syncMediaUi(root, media);
  if (media.kind === "audio") persistAudioProgress(root, media, true);
  else persistVideoProgress(root, media, true);
}

export function unmountMediaPlayer(root: Root, kind: MediaKind, options: { force?: boolean } = {}) {
  const container = pickContainer(root, kind);
  if (kind === 'audio') paintContentTraceLabel(container, undefined, 'audio');
  const media = mounted.get(container);
  persistBeforeDispose(root, media);
  // P1-11: 取消挂起的 rAF, 避免 unmount 后仍触发 syncMediaUi 写入已移除的 DOM。
  if (media) {
    const pendingHandle = uiSyncPending.get(media);
    if (pendingHandle !== undefined) {
      cancelAnimationFrame(pendingHandle);
      uiSyncPending.delete(media);
    }
  }
  if (!options.force && kind === "video" && media?.element instanceof HTMLVideoElement && isVideoPictureInPictureActive(media.element)) {
	media.pictureInPictureExitCleanup?.();
	let cleanup: () => void;
	cleanup = observeVideoPictureInPictureExit(media.element, () => {
      const vf = (root instanceof Document ? root : root.ownerDocument ?? document).getElementById(playerSelectors.video.container);
      media.element.controls = false;
		if (media.pictureInPictureExitCleanup === cleanup) media.pictureInPictureExitCleanup = undefined;
      if (vf?.classList.contains("active")) {
        return;
      }
      persistBeforeDispose(root, media);
      disposeMedia(media, true);
      mounted.delete(container);
      if (activeVideoContainer === container) activeVideoContainer = null;
		});
	media.pictureInPictureExitCleanup = cleanup;
    return;
  }
  disposeMedia(media, true);
  mounted.delete(container);
	if (kind === 'audio') {
		invalidateAudioHandoffIntent(root);
		unregisterAudioPlaybackOwner(root as unknown as object);
	}
  sweepStrayMediaElements(container, kind);
  if (kind === "audio" && activeAudioContainer === container) activeAudioContainer = null;
  if (kind === "video" && activeVideoContainer === container) activeVideoContainer = null;
	if (kind === 'audio') {
		clearNowPlayingMedia(root, media);
		clearOptimisticAudioPlayback(root);
		resetAudioPlaybackUi(root, media?.root);
	}
}

/**
 * A Watch click transfers playback ownership. It differs from ordinary SPA
 * navigation: a different old video session must be stopped and released so a
 * detached PiP element cannot continue as a second audible track.
 */
export function replaceMountedVideoForExplicitTarget(root: Root, target: { topicId: string; chapterIndex: number }): boolean {
	const media = mountedFor(root, 'video');
	if (!media || media.kind !== 'video' || (media.topicId === target.topicId && media.chapterIndex === target.chapterIndex)) return false;
	teardownMountedVideoSession(root, media);
	return true;
}

/**
 * Audio ownership closes a live system PiP video session exactly like an
 * explicit Watch does. The later audio mount disposes the other kind's
 * element, and a disposed-but-still-presented video strands an orphan black
 * floating window that no page code can address afterwards.
 */
export function closeMountedVideoPictureInPictureSession(root: Root): boolean {
	const media = mountedFor(root, 'video');
	if (!media || media.kind !== 'video' || !(media.element instanceof HTMLVideoElement) || !isVideoPictureInPictureActive(media.element)) return false;
	teardownMountedVideoSession(root, media);
	return true;
}

function teardownMountedVideoSession(root: Root, media: MountedPlayer): void {
	const container = media.root;
	persistBeforeDispose(root, media);
	if (media.element instanceof HTMLVideoElement) {
		cancelVideoPictureInPictureExitHandoff(media.element);
		setVideoPictureInPictureExplicitPlaybackIntent(media.element, false);
		// Remove the exit observers first: a live observer would answer the
		// programmatic exit below by restoring the video surface over whatever
		// view the user is on.
		media.pictureInPictureExitCleanup?.();
		media.pictureInPictureExitCleanup = undefined;
		signalNativeFullscreenPlaybackIntent(media.element, false);
		media.element.pause();
		// The browser surface closes asynchronously. Always continue with source
		// and lease cleanup even when its exit API rejects.
		void exitVideoPictureInPicturePresentation(media.element).catch(() => undefined);
	}
	disposeMedia(media, true);
	mounted.delete(container);
	if (activeVideoContainer === container) activeVideoContainer = null;
	sweepStrayMediaElements(container, 'video');
}

export function getMountedMediaDebugInfo(root: Root) {
  return (["audio", "video"] as const).map((kind) => {
    const media = mounted.get(pickContainer(root, kind));
    if (!media) return { kind, hasElement: false, hasSrc: false, urlPreview: null, expiresAt: 0 };
    return {
      kind,
      hasElement: !!media.element,
      hasSrc: !!media.element.src,
      urlPreview: media.url ? `${media.url.slice(0, 60)}…` : null,
      expiresAt: media.expiresAt,
    };
  });
}

export function isExpired(media: MountedPlayer) {
  return media.expiresAt > 0 && Date.now() / 1000 > media.expiresAt - 30;
}

function wireMediaEvents(root: Root, media: MountedPlayer) {
  media.element.addEventListener("play", () => {
		if (mountedFor(root, media.kind) !== media) return;
    if (playbackCurrentSeconds(media) < playbackDurationSeconds(media)) media.endBoundaryDispatched = false;
    pauseOtherKind(root, media.kind);
    syncMediaUi(root, media);
  });
	media.element.addEventListener('playing', () => {
		if (mountedFor(root, media.kind) !== media) return;
		if (media.kind === 'audio' && isAudioHandoffCancelled(root)) {
			recordPlaybackPausedHandoffCancel({ mediaKind: 'audio' });
			media.element.pause();
			releaseMediaLeaseBestEffort(media);
			return;
		}
		if (media.kind === 'audio' && !observeAudioPlaying(root, media) && media.element.paused) return;
		if (media.kind === 'audio') syncNowPlayingMedia(root, media);
		if (media.kind === 'audio' && media.claimOwnerOnNextPlaying) {
			media.claimOwnerOnNextPlaying = false;
			claimAudioPlaybackOwner(root as unknown as object);
		}
		const api = runtimeOf(root)?.api;
		if (api?.heartbeatPlaybackLease) startMediaLeaseHeartbeat(media, (playbackLeaseId) => api.heartbeatPlaybackLease(playbackLeaseId));
		// Keep the API client receiver. Passing the method directly loses `this`,
		// so its internal `this.post(...)` throws as soon as audio starts.
		if (media.kind === 'audio' && api?.markAudioPlaybackStarted) {
			requestAudioPlaybackStarted(media, (playbackLeaseId) => api.markAudioPlaybackStarted(playbackLeaseId));
		}
		bindMediaSession(root, media);
	});
	media.element.addEventListener("pause", () => {
		if (mountedFor(root, media.kind) !== media) return;
		syncMediaUi(root, media);
	});
	media.element.addEventListener("pause", () => {
		if (mountedFor(root, media.kind) !== media) return;
		persistBeforeDispose(root, media);
	});
	media.element.addEventListener("ended", () => {
		if (mountedFor(root, media.kind) !== media) return;
		syncMediaUi(root, media);
    persistBeforeDispose(root, media);
		if (media.kind === 'audio' && handoffPreparedAudioOnEnded(root, media)) return;
		if (media.kind === 'video') media.suppressEndedMediaSessionCleanup = true;
		if (media.element instanceof HTMLVideoElement) cancelVideoPictureInPictureExitHandoff(media.element);
    releaseMediaLeaseBestEffort(media);
    dispatchMediaEnded(root, media);
  });
  media.element.addEventListener("timeupdate", () => {
		if (mountedFor(root, media.kind) !== media) return;
		if (media.kind === 'audio' && isAudioHandoffTransitioning(root, media)) return;
    // rAF 合并: timeupdate 每秒触发 ~4 次, 多次事件合并到单帧 DOM 写入
    scheduleUiSync(root, media);
    if (media.kind === "audio") persistAudioProgress(root, media);
    else persistVideoProgress(root, media);
		if (media.kind === 'audio') onAudioTimeupdate(root, media);
    if (media.kind === "video") {
      maybeFinalChapterBurst(root, {
        currentTime: media.element.currentTime,
        duration: media.element.duration,
        pause: () => media.element.pause(),
      });
    }
  });
	media.element.addEventListener("loadedmetadata", () => {
		if (mountedFor(root, media.kind) !== media) return;
		if (media.kind === 'audio' && isAudioHandoffTransitioning(root, media)) return;
    if (typeof media.virtualCurrentTime === "number" && media.virtualCurrentTime > 0) {
      const duration = playbackDurationSeconds(media);
      if (duration > 0) setPlaybackCurrentSeconds(media, Math.min(duration, media.virtualCurrentTime));
    }
		if (media.kind === "audio") restoreAudioPosition(root, media);
		else restoreVideoPosition(root, media);
    syncMediaUi(root, media);
		if (media.element instanceof HTMLVideoElement && mountedFor(root, 'video') === media) {
			syncVideoPresentationControls(root, media.element);
		}
	});
	media.element.addEventListener('loadstart', () => {
		if (mountedFor(root, media.kind) !== media) return;
		if (media.element instanceof HTMLVideoElement) syncVideoPresentationControls(root);
		if (media.kind === 'audio') onAudioHandoffLoadStart(root, media);
	});
	media.element.addEventListener('emptied', () => {
		if (mountedFor(root, media.kind) !== media) return;
		if (media.element instanceof HTMLVideoElement) syncVideoPresentationControls(root);
	});
	for (const type of ['error', 'abort'] as const) {
		media.element.addEventListener(type, () => {
			if (mountedFor(root, media.kind) !== media) return;
			if (type === 'error') recordMediaElementError(playbackFailureInfo(media, media.kind, undefined));
			if (media.kind === 'audio' && canFailAudioHandoff(root, media)) failAudioHandoff(root, media, `handoff_${type}`);
			if (media.kind === 'video' && type === 'error' && mountedFor(root, 'video') === media && !media.loading) {
				void recoverVideoPlayback(root, media, 'media_error');
			}
		});
	}
	media.element.addEventListener('stalled', () => {
		if (mountedFor(root, media.kind) !== media) return;
		if (media.kind === 'audio') markAudioHandoffStalled(root, media);
		if (media.kind === 'video' && mountedFor(root, 'video') === media && !media.loading && !media.videoRecoveryAttempted) {
			scheduleVideoStallRecovery(root, media);
		}
	});
}

function dispatchMediaEnded(root: Root, media: MountedPlayer): void {
	if (mountedFor(root, media.kind) !== media || !media.topicId || !media.chapterIndex) return;
	if (media.endBoundaryDispatched) return;
	media.endBoundaryDispatched = true;
	(root as Document | HTMLElement).dispatchEvent(new CustomEvent<MediaEndedEventDetail>('deepmox:media-ended', {
		detail: {
			kind: media.kind,
			topicId: media.topicId,
			chapterIndex: media.chapterIndex,
			element: media.element,
		},
	}));
}

function pauseOtherKind(root: Root, current: MediaKind): void {
  const other = mountedFor(root, current === "audio" ? "video" : "audio");
	if (other?.kind === 'audio') invalidateFrozenAudioStartRecovery(other);
	if (other?.element instanceof HTMLVideoElement) {
		cancelVideoPictureInPictureExitHandoff(other.element);
		setVideoPictureInPictureExplicitPlaybackIntent(other.element, false);
		signalNativeFullscreenPlaybackIntent(other.element, false);
	}
  if (other && !other.element.paused) {
		other.element.pause();
	}
}

// rAF 合并 timeupdate 的 DOM 写入 — 每帧最多一次 syncMediaUi
const uiSyncPending = new WeakMap<MountedPlayer, number>();
function scheduleUiSync(root: Root, media: MountedPlayer): void {
  if (uiSyncPending.has(media)) return;
  const handle = requestAnimationFrame(() => {
    uiSyncPending.delete(media);
		// A replacement source may have committed between timeupdate and this
		// frame. Never allow its old callback to repaint the new player's UI.
		if (mountedFor(root, media.kind) !== media) return;
    syncMediaUi(root, media);
  });
  uiSyncPending.set(media, handle);
}

function persistBeforeDispose(root: Root, media: MountedPlayer | undefined): void {
  if (!media) return;
	if (media.kind === 'audio' && (isAudioHandoffTransitioning(root, media) || isAudioHandoffCancelled(root))) return;
  if (media.kind === "audio") persistAudioProgress(root, media, true);
  else persistVideoProgress(root, media, true);
}

/**
 * The server-side grant claim is idempotent. Retry a small finite number of
 * times for a lost response, but never let a previous source mark a later
 * chapter's lease after a handoff/remount.
 */
function requestAudioPlaybackStarted(media: MountedPlayer, markStarted: ((leaseId: string) => Promise<unknown>) | undefined): void {
	const leaseId = media.playbackLeaseId;
	if (!leaseId || !markStarted || (media.engagementStartRequested && media.engagementStartLeaseId === leaseId)) return;
	media.engagementStartRequested = true;
	media.engagementStartLeaseId = leaseId;
	media.engagementStartAttempts = 0;
	const attempt = () => {
		if (media.playbackLeaseId !== leaseId || media.releaseStarted) return;
		void markStarted(leaseId).then(() => {
			if (media.playbackLeaseId === leaseId) media.engagementStartRetryTimer = undefined;
		}).catch(() => {
			if (media.playbackLeaseId !== leaseId || media.releaseStarted) return;
			const attempts = (media.engagementStartAttempts ?? 0) + 1;
			media.engagementStartAttempts = attempts;
			if (attempts >= 3) {
				media.engagementStartRequested = false;
				media.engagementStartLeaseId = undefined;
				return;
			}
			media.engagementStartRetryTimer = globalThis.setTimeout(attempt, attempts * 300) as unknown as number;
		});
	};
	attempt();
}

export function mountedFor(root: Root, kind: MediaKind): MountedPlayer | undefined {
  const active = kind === "audio" ? activeAudioContainer : activeVideoContainer;
  if (active) {
    const media = mounted.get(active);
    if (media?.kind === kind) return media;
  }
  return mounted.get(pickContainer(root, kind));
}

/**
 * Remove same-kind media elements the mount registry no longer tracks. The
 * registry is the only thing pause/unmount look at, so an element that lands
 * in the container outside a tracked mount (article-view rebuilds and rapid
 * chapter transitions have both produced this) keeps playing audibly with no
 * code path able to stop it. Every direct-child media element that is not the
 * kept mount is paused and detached here.
 */
function sweepStrayMediaElements(container: HTMLElement, kind: MediaKind, keep?: MountedPlayer): void {
  for (const child of [...container.children]) {
    if (child !== keep?.element && matchesMediaKind(child, kind)) {
      child.pause();
      child.removeAttribute('src');
      child.load();
      child.remove();
    }
  }
}

function matchesMediaKind(element: Element, kind: MediaKind): element is HTMLAudioElement | HTMLVideoElement {
  if (kind === 'audio') return element instanceof HTMLAudioElement;
  return element instanceof HTMLVideoElement;
}

/** Stop-and-detach stray elements of `kind` while keeping the mounted one. */
export function sweepStrayMedia(root: Root, kind: MediaKind): void {
  sweepStrayMediaElements(pickContainer(root, kind), kind, mountedFor(root, kind));
}

function setActiveContainer(kind: MediaKind, container: HTMLElement) {
  if (kind === "audio") activeAudioContainer = container;
  else activeVideoContainer = container;
}

function releaseOtherMediaKind(root: Root, currentKind: MediaKind): void {
	const otherKind: MediaKind = currentKind === 'audio' ? 'video' : 'audio';
	const otherContainer = pickContainer(root, otherKind);
	const other = mounted.get(otherContainer);
	if (!other || other.kind !== otherKind) return;
	persistBeforeDispose(root, other);
	disposeMedia(other, true);
	mounted.delete(otherContainer);
	if (otherKind === 'audio') {
		if (activeAudioContainer === otherContainer) activeAudioContainer = null;
		clearNowPlayingMedia(root, other);
		clearOptimisticAudioPlayback(root);
		resetAudioPlaybackUi(root, other.root);
	}
	if (otherKind === 'video' && activeVideoContainer === otherContainer) activeVideoContainer = null;
}

function wirePagehideRelease(media: MountedPlayer): void {
  if (typeof window === 'undefined') return;
  media.pagehideHandler = (event?: PageTransitionEvent) => {
		// bfcache is a suspension, not an explicit close; pageshow resumes the
		// same player/lease. A real navigation still releases promptly.
		if (!event?.persisted) releaseMediaLeaseBestEffort(media);
	};
	window.addEventListener('pagehide', media.pagehideHandler);
}

function disposeMedia(media: MountedPlayer | undefined, release: boolean, preserveMediaSession = false): void {
  if (!media) return;
	invalidateFrozenAudioStartRecovery(media);
	const pendingHandle = uiSyncPending.get(media);
	if (pendingHandle !== undefined) {
		cancelAnimationFrame(pendingHandle);
		uiSyncPending.delete(media);
	}
	clearVideoStallRecovery(media);
	if (media.blobSourceUrl) {
		URL.revokeObjectURL(media.blobSourceUrl);
		media.blobSourceUrl = undefined;
	}
	if (media.element instanceof HTMLVideoElement) {
		cancelVideoPictureInPictureExitHandoff(media.element);
		media.element.dispatchEvent(new Event(NATIVE_FULLSCREEN_DISPOSE_EVENT));
	}
	media.pictureInPictureExitCleanup?.();
	media.pictureInPictureExitCleanup = undefined;
	if (media.element instanceof HTMLVideoElement && isVideoPictureInPictureActive(media.element)) {
		// Clearing the source of the element the browser still presents strands
		// an orphan black PiP window that no page code can address afterwards.
		// Exit the presentation first; it closes even when this rejects.
		void exitVideoPictureInPicturePresentation(media.element).catch(() => undefined);
	}
	if (!preserveMediaSession) media.mediaSessionCleanup?.();
	media.mediaSessionCleanup = undefined;
  if (release) releaseMediaLeaseBestEffort(media);
  else stopMediaLeaseHeartbeat(media);
  if (media.pagehideHandler && typeof window !== 'undefined') window.removeEventListener('pagehide', media.pagehideHandler);
  media.pagehideHandler = undefined;
  disposeMounted(media);
}

/** Clears an ended video session only after final/gate/failed end decisions. */
export function clearVideoEndedMediaSession(root: Root, element?: HTMLVideoElement | HTMLAudioElement): void {
	const media = mountedFor(root, 'video');
	if (!media || (element && media.element !== element)) return;
	media.suppressEndedMediaSessionCleanup = false;
	media.mediaSessionCleanup?.();
	media.mediaSessionCleanup = undefined;
}

function bindMediaSession(root: Root, media: MountedPlayer): void {
	// Artwork is resolved at every bind (including each `playing` event), so a
	// topic that loaded after mount still reaches the lock screen, and chapter
	// handoff keeps the same cover without touching sessionMetadata.
	const artwork = mediaSessionArtworkFor(media);
	if (media.sessionMetadata) {
		media.mediaSessionCleanup = mediaSessionController.mount(media, {
			metadata: { ...media.sessionMetadata, artwork },
			onPlay: () => { if (media.element instanceof HTMLVideoElement) setVideoPictureInPictureExplicitPlaybackIntent(media.element, true); void playMediaPlayer(root, media.kind); },
			onPause: () => pauseMediaPlayerFromUser(root, media.kind),
			onPrevious: () => dispatchMediaChapterCommand(root, media.kind, 'prev'),
			onNext: () => dispatchMediaChapterCommand(root, media.kind, 'next'),
			onEnterPictureInPicture: () => requestBrowserInitiatedVideoPictureInPicture(media),
		});
		return;
	}
	const active = (root as unknown as {
		__nowPlayingChapter?: { title?: string; topicTitle?: string };
		__activeChapter?: { title?: string; topicTitle?: string };
	}).__nowPlayingChapter ?? (root as unknown as {
		__activeChapter?: { title?: string; topicTitle?: string };
	}).__activeChapter;
	media.mediaSessionCleanup = mediaSessionController.mount(media, {
		metadata: {
			title: active?.title || active?.topicTitle || 'Chapter',
			artist: active?.topicTitle || 'DeepMox',
			album: 'DeepMox',
			artwork,
		},
		onPlay: () => { if (media.element instanceof HTMLVideoElement) setVideoPictureInPictureExplicitPlaybackIntent(media.element, true); void playMediaPlayer(root, media.kind); },
		onPause: () => pauseMediaPlayerFromUser(root, media.kind),
		onPrevious: () => dispatchMediaChapterCommand(root, media.kind, 'prev'),
		onNext: () => dispatchMediaChapterCommand(root, media.kind, 'next'),
		onEnterPictureInPicture: () => requestBrowserInitiatedVideoPictureInPicture(media),
	});
}

function requestBrowserInitiatedVideoPictureInPicture(media: MountedPlayer): void {
	if (media.kind !== 'video' || !(media.element instanceof HTMLVideoElement)) return;
	if (isVideoPictureInPictureActive(media.element)) return;
	// This handler is invoked by a browser's Media Session Auto PiP policy,
	// rather than from visibilitychange. The latter is commonly rejected once
	// a page is hidden and would create noisy, unreliable prompts on mobile.
	void toggleVideoPictureInPicturePresentation(media.element).catch(() => undefined);
}

function dispatchMediaChapterCommand(root: Root, kind: MediaKind, direction: 'prev' | 'next'): void {
	(root as Document | HTMLElement).dispatchEvent?.(new CustomEvent(`deepmox:${kind}-${direction}`));
}

async function preparePictureInPictureTransition(
	root: Root,
	media: MountedPlayer,
	surface: MediaSurface,
	options: PrepareVideoMediaPlayerTransitionOptions,
): Promise<MediaChapterTransitionCommit> {
	const element = media.element as HTMLVideoElement;
	cancelVideoPictureInPictureExitHandoff(element);
	// PiP cannot stage a detached candidate: it must reuse its visible video
	// element. Give each source write an owner so an older recovery cannot roll
	// back a chapter handoff that won the race on the same element.
	const operationGeneration = (media.videoSourceOperationGeneration ?? 0) + 1;
	media.videoSourceOperationGeneration = operationGeneration;
	const ownsOperation = () => mountedFor(root, 'video') === media
		&& media.videoSourceOperationGeneration === operationGeneration;
	clearVideoStallRecovery(media);
	const previous = {
		src: element.src,
		currentTime: element.currentTime,
		paused: element.paused,
		loading: media.loading,
		url: media.url,
		topicId: media.topicId,
		chapterIndex: media.chapterIndex,
		expiresAt: media.expiresAt,
		playbackLeaseId: media.playbackLeaseId,
		heartbeatIntervalSeconds: media.heartbeatIntervalSeconds,
		traceCode: media.traceCode,
		fallbackDurationSeconds: media.fallbackDurationSeconds,
		virtualCurrentTime: media.virtualCurrentTime,
		lastProgressSyncAt: media.lastProgressSyncAt,
		lastProgressPercent: media.lastProgressPercent,
		resumeApplied: media.resumeApplied,
		releaseStarted: media.releaseStarted,
	};
	const ready = await loadVideoCandidate(element, surface.signedPlaybackUrl, options.signal, options.timeoutMs);
	const mayCommit = ready && !options.signal?.aborted && options.canCommit?.() !== false;
	if (!mayCommit) {
		if (!ownsOperation()) return { committed: false };
		Object.assign(media, {
			url: previous.url,
			topicId: previous.topicId,
			chapterIndex: previous.chapterIndex,
			expiresAt: previous.expiresAt,
			loading: previous.loading,
			playbackLeaseId: previous.playbackLeaseId,
			heartbeatIntervalSeconds: previous.heartbeatIntervalSeconds,
			traceCode: previous.traceCode,
			fallbackDurationSeconds: previous.fallbackDurationSeconds,
			virtualCurrentTime: previous.virtualCurrentTime,
			lastProgressSyncAt: previous.lastProgressSyncAt,
			lastProgressPercent: previous.lastProgressPercent,
			resumeApplied: previous.resumeApplied,
			releaseStarted: previous.releaseStarted,
		});
		restorePictureInPictureElement(element, previous.src, previous.currentTime, previous.paused);
		return { committed: false };
	}
	if (!ownsOperation() || options.canCommit?.() === false) return { committed: false };

	stopMediaLeaseHeartbeat(media);
	const rt = runtimeOf(root);
	media.url = surface.signedPlaybackUrl;
	media.topicId = options.topicId ?? rt?.activeCourseId;
	media.chapterIndex = options.chapterIndex ?? rt?.activeChapterIndex;
	media.expiresAt = Math.floor(Date.now() / 1000) + surface.expiresInSeconds;
	media.playbackLeaseId = surface.playbackLeaseId;
	media.heartbeatIntervalSeconds = surface.heartbeatIntervalSeconds;
	media.traceCode = surface.watermark?.traceCode;
	media.releaseStarted = false;
	media.loading = false;
	media.fallbackDurationSeconds = activeVideoDurationFallback(root);
	media.virtualCurrentTime = undefined;
	media.lastProgressPercent = undefined;
	media.lastProgressSyncAt = undefined;
	media.resumeApplied = false;
	media.sourceGeneration = (media.sourceGeneration ?? 0) + 1;
	media.videoRecoveryAttempted = false;
	media.suppressEndedMediaSessionCleanup = false;
	restoreVideoPosition(root, media);
	if (surface.watermark?.traceCode) updateVideoWatermarkTrace(root, surface.watermark.traceCode);
	syncMediaUi(root, media);
	bindMediaSession(root, media);
	return {
		committed: true,
		previousLeaseId: previous.playbackLeaseId && previous.playbackLeaseId !== media.playbackLeaseId
			? previous.playbackLeaseId
			: undefined,
	};
}

function loadVideoCandidate(
	element: HTMLVideoElement,
	src: string,
	signal: AbortSignal | undefined,
	timeoutMs = 10_000,
): Promise<boolean> {
	return new Promise((resolve) => {
		let settled = false;
		const finish = (ready: boolean) => {
			if (settled) return;
			settled = true;
			clearTimeout(timeoutId);
			element.removeEventListener('loadedmetadata', onReady);
			element.removeEventListener('canplay', onReady);
			element.removeEventListener('error', onError);
			signal?.removeEventListener('abort', onAbort);
			resolve(ready);
		};
		const onReady = () => finish(true);
		const onError = () => finish(false);
		const onAbort = () => finish(false);
		const timeoutId = setTimeout(() => finish(false), Math.max(1, timeoutMs));
		element.addEventListener('loadedmetadata', onReady, { once: true });
		element.addEventListener('canplay', onReady, { once: true });
		element.addEventListener('error', onError, { once: true });
		signal?.addEventListener('abort', onAbort, { once: true });
		if (signal?.aborted) {
			finish(false);
			return;
		}
		try {
			element.src = src;
			element.load();
		} catch {
			finish(false);
		}
	});
}

function discardVideoCandidate(candidate: HTMLVideoElement): void {
	candidate.removeAttribute('src');
	try { candidate.load(); } catch { /* The detached candidate has no remaining lifecycle. */ }
	candidate.remove();
}

function restorePictureInPictureElement(
	element: HTMLVideoElement,
	src: string,
	currentTime: number,
	paused: boolean,
): void {
	const restorePlaybackState = () => {
		try {
			element.currentTime = currentTime;
			if (!paused) void element.play().catch(() => undefined);
		} catch {
			// Safari may reject currentTime until the restored source has metadata.
		}
	};
	try {
		element.addEventListener('loadedmetadata', restorePlaybackState, { once: true });
		element.src = src;
		element.load();
		restorePlaybackState();
	} catch {
		element.removeEventListener('loadedmetadata', restorePlaybackState);
		// The mounted identity and old lease remain unchanged even if the browser rejects source restoration.
	}
}
